<script lang="js">
import axios from "axios";
import Swal from "sweetalert2";
import HelperFunctions from "@/utils/HelperFunctions";
import { jsPDF } from 'jspdf';
import { Modal } from 'bootstrap';
import logo from '../../assets/logo.png';

export default {
  data() {
    return {
      logger: null,
      sessionYear: "",
      hp: new HelperFunctions(),
      surname: "",
      firstname: "",
      othername: "",
      email: "",
      phone: "",
      password: "",
      confirmPassword: "",
      session: "",
      programTypes: [],
      programModes: [],
      programType: '',
      programMode: '',
      program: '',
      availablePrograms: [],
      academicSessions: [],
      loading: false,
      errorMessage: "",
      fetchingPrograms: false,
      applicationAvailable: false,
      // Add new data properties for login details
      loginDetails: {
        fullName: '',
        email: '',
        password: '',
        applicationNumber: ''
      },
      displayModal: null
    };
  },
  created() {
    // Create a logger instance for this component
    this.logger = this.$createLogger('AppRegister');
  },
  methods: {
    async captureAdmissionPartnerReferral() {
      const rawCode = Array.isArray(this.$route.query.ref)
        ? this.$route.query.ref[0]
        : this.$route.query.ref;
      const code = String(rawCode || "").trim();
      if (!code) return;

      const nextQuery = { ...this.$route.query };
      delete nextQuery.ref;
      this.$router.replace({ path: this.$route.path, query: nextQuery }).catch(() => {});

      try {
        await axios.post(`${process.env.VUE_APP_API_BASE_URL}/referrals/visit`, {
          code,
          landingPath: this.$route.path,
          campaign: {
            source: this.$route.query.utm_source,
            medium: this.$route.query.utm_medium,
            campaign: this.$route.query.utm_campaign,
          },
        });
      } catch (error) {
        // Referral analytics must never prevent an applicant from registering.
        this.logger.warn("Referral tracking was unavailable", error);
      }
    },
    resetForm() {
      this.surname = "";
      this.firstname = "";
      this.othername = "";
      this.email = "";
      this.programType = "";
      this.programMode = "";
      this.program = "";
      this.phone = "";
      this.password = "";
      this.confirmPassword = "";
      this.session = "";
      this.availablePrograms = [];
    },
    showLoginDetails() {
      if (!this.displayModal) {
        this.displayModal = new Modal(document.getElementById('displayModal'));
      }
      // Add event listener for modal close
      document.getElementById('displayModal').addEventListener('hidden.bs.modal', this.onModalClose);
      this.displayModal.show();
    },

    onModalClose() {
      // Refresh the application period check
      this.fetchActiveAcademicSession();
      // Remove the event listener to prevent memory leaks
      document.getElementById('displayModal').removeEventListener('hidden.bs.modal', this.onModalClose);
    },
    copyToClipboard(text) {
      navigator.clipboard.writeText(text).then(() => {
        Swal.fire({
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 2000,
          icon: 'success',
          title: 'Copied to clipboard!'
        });
      });
    },
    downloadPDF() {
      const doc = new jsPDF();

      // Add FCP logo
      doc.addImage(logo, 'PNG', 95, 17, 20, 15);

      // Document Title
      doc.setFontSize(18);
      doc.text(`${process.env.VUE_APP_FCP_NAME}`, 105, 40, { align: 'center' });

      doc.setFontSize(14);
      doc.text('Registration Confirmation', 105, 50, { align: 'center' });

      doc.setFontSize(12);
      doc.text(`Date: ${new Date().toLocaleDateString()}`, 20, 65);

      // Greeting
      doc.setFontSize(12);
      doc.text(`Dear ${this.hp.toTitleCase(this.loginDetails.name)},`, 20, 80);

      // Intro Message
      doc.text(
        `Thank you for registering with ${process.env.VUE_APP_FCP_NAME}. Your application has been received successfully.`,
        20,
        90,
        { maxWidth: 170 }
      );

      // Credentials Section
      doc.text('Your login credentials:', 20, 105);
      doc.text(
        [
          `Application Number: ${this.loginDetails.applicationNumber}`,
          "Password: the password you created during registration",
        ],
        20,
        115
      );

      doc.setFontSize(10);
      doc.text('Please keep these credentials safe and secure. You will need them to access the student portal.', 20,
        125, { maxWidth: 170 });


      doc.setFontSize(12);
      // Credential reminder
      doc.text(
        `Save this document now and keep your application number secure.`,
        20,
      135,
        { maxWidth: 170 }
      );

      // Support Info
      doc.text(
        `If you have any questions, please contact our admissions office at ${process.env.VUE_APP_FCP_ADMISSIONS_EMAIL}.`,
        20,
        145,
        { maxWidth: 170 }
      );

      // Closing
      doc.text(`Thank you for your interest in ${process.env.VUE_APP_FCP_NAME}.`, 20, 165);
      doc.text('Regards,', 20, 175);
      doc.text(`${process.env.VUE_APP_FCP_NAME}`, 20, 180);


      // Save the PDF
      doc.save(`FCP_Registration_${this.loginDetails.applicationNumber}.pdf`);
    },
    async fetchProgramTypes() {
      try {
        const response = await axios.get(`${process.env.VUE_APP_API_BASE_URL}/programtypes`);
        this.programTypes = response.data;
      } catch (err) {
        this.logger.error('Failed to fetch program types:', err);
        this.errorMessage = 'Failed to load program types data';
      }
    },
    async fetchProgramModes() {
      try {
        const response = await axios.get(`${process.env.VUE_APP_API_BASE_URL}/programmodes`);
        this.programModes = response.data;
      } catch (err) {
        this.logger.error('Failed to fetch program modes:', err);
        this.errorMessage = 'Failed to load program modes data';
      }
    },
    async fetchActiveAcademicSession() {
      try {
        const response = await axios.get(`${process.env.VUE_APP_API_BASE_URL}/academic-sessions/public/active`);
        if (response.data && response.data.length > 0) {
          this.logger.info('Fetched academic sessions:', JSON.stringify(response.data, null, 2));
          const activeSession = response.data.find(session =>
            session.status === 'open' &&
            session.control &&
            session.control.controls &&
            session.control.controls.some(control =>
              control.controlName === 'application' && control.active
            )
          );

          if (activeSession) {
            this.session = activeSession._id;
            this.sessionYear = activeSession.sessionYear;
            this.applicationAvailable = true;
            this.logger.success('Found active application period:', activeSession.sessionYear);
          } else {
            this.errorMessage = 'No active application period is currently available';
            this.applicationAvailable = false;
            this.logger.warn('No active application period found');
          }
        } else {
          this.errorMessage = 'No active academic sessions available';
          this.applicationAvailable = false;
          this.logger.warn('No academic sessions available');
        }
      } catch (err) {
        this.logger.error('Failed to fetch academic sessions:', err);
        this.errorMessage = 'Failed to load academic sessions data';
        this.applicationAvailable = false;
      }
    },
    async fetchProgramsByCriteria() {
      if (!this.programType || !this.programMode) return;

      this.fetchingPrograms = true;
      this.availablePrograms = [];
      this.program = '';

      try {
        // Get the selected program type and mode objects
        const selectedType = this.programTypes.find(type => type._id === this.programType);
        const selectedMode = this.programModes.find(mode => mode._id === this.programMode);

        if (!selectedType || !selectedMode) {
          throw new Error('Invalid program type or mode selection');
        }

        // Fetch program offerings based on the selected type and mode
        const response = await axios.get(
          `${process.env.VUE_APP_API_BASE_URL}/program-offerings`,
          {
            params: {
              type: this.programType, // Use the selected type ID directly
              mode: this.programMode  // Use the selected mode ID directly
            }
          }
        );

        // Extract unique programs from the offerings
        const offerings = response.data;
        const uniquePrograms = [];

        offerings.forEach(offering => {
          // Check if the offering has the correct type and mode in its structure
          // AND filter out inactive offerings and programs
          if (offering.structure &&
            offering.structure.type._id === this.programType &&
            offering.structure.mode._id === this.programMode &&
            offering.program &&
            offering.active !== false && // Only include active offerings
            offering.program.active !== false && // Only include programs that are active
            !uniquePrograms.some(p => p._id === offering.program._id)) {
            uniquePrograms.push({
              _id: offering.program._id,
              name: offering.program.name,
              offeringId: offering._id, // Store the offering ID for submission
              type: offering.structure.type.type,
              mode: offering.structure.mode.mode
            });
          }
        });

        // Sort programs alphabetically by name
        this.availablePrograms = uniquePrograms.sort((a, b) => a.name.localeCompare(b.name));

        if (uniquePrograms.length === 0) {
          this.errorMessage = 'No programs available for the selected type and mode';
        }
      } catch (err) {
        this.logger.error('Error fetching programs:', err);
        this.errorMessage = 'Failed to load program options';
      } finally {
        this.fetchingPrograms = false;
      }
    },
    async submitForm() {
      // Validate email before submission
      if (!this.hp.isValidEmail(this.email)) {
        this.logger.info("Please enter a valid email address.");
        this.errorMessage = "Please enter a valid email address.";
        return;
      }

      // Validate program selection
      if (!this.programType || !this.programMode || !this.program) {
        this.errorMessage = "Please select a valid program.";
        return;
      }

      // Validate that an active session was found
      if (!this.session) {
        this.errorMessage = "No active application period is currently available. Please try again later.";
        return;
      }

      // Find the selected program from available programs
      const selectedProgram = this.availablePrograms.find(p => p._id === this.program);
      if (!selectedProgram) {
        this.errorMessage = "Invalid program selection.";
        return;
      }

      const payload = {
        lastName: this.surname,
        firstName: this.firstname,
        otherName: this.othername,
        email: this.email,
        phone: this.phone.toString(),
        programOfferingId: selectedProgram.offeringId,
        password: this.password,
        confirmPassword: this.confirmPassword,
      };

      this.loading = true;
      this.errorMessage = "";

      try {
        const apiUrl = `${process.env.VUE_APP_API_BASE_URL}/applications/register`;
        const response = await axios.post(
          apiUrl,
          payload
        );

        // Check if the response indicates success
        if (response.data.success) {
          // Store login details from response
          this.loginDetails = {
            name: `${response.data.data.user.firstName} ${response.data.data.user.otherName || ""} ${response.data.data.user.lastName}`,
            email: response.data.data.user.email,
            applicationNumber: response.data.data.application.applicationNumber
          };

          Swal.fire({
            title: "Successful!",
            text: "Registration successful! Your login details will be displayed next.",
            icon: "success"
          }).then(() => {
            // Show the modal with login details
            this.showLoginDetails();
            // Clear the form after showing the modal
            this.resetForm();
          });
        } else {
          // If the response indicates failure
          this.errorMessage = response.data.message || "Registration failed. Please try again.";
        }
      } catch (error) {
        this.logger.error("Registration error:", error);
        this.errorMessage = error.response?.data?.message || "An error occurred during registration. Please try again.";
      } finally {
        this.loading = false;
      }
    },

  },
  computed: {
  },
  async mounted() {
    // Initialize Bootstrap modal
    this.displayModal = new Modal(document.getElementById('displayModal'));

    await this.captureAdmissionPartnerReferral();

    // Fetch all required data
    await Promise.all([
      this.fetchProgramTypes(),
      this.fetchProgramModes(),
      this.fetchActiveAcademicSession()
    ]);
  },
  beforeUnmount() {
    // Clean up event listeners
    if (this.displayModal) {
      document.getElementById('displayModal')?.removeEventListener('hidden.bs.modal', this.onModalClose);
    }
  }
};
</script>

<template>
  <div class="container-fluid bg-body-tertiary">
    <div class="d-flex align-items-center py-4">
      <main class="m-auto">
        <form @submit.prevent="submitForm">
          <RouterLink to="/">
            <img
              src="@/assets/logo.png"
              class="mb-4"
              alt=""
              width="96.26"
              height="70"
            />
          </RouterLink>
          <h3 class="mb-5 fw-bold text-uppercase">
            FCP ONLINE APPLICATION {{ sessionYear }}
          </h3>

          <div class="d-flex border-bottom border-dark-subtle mb-4">
            <h6 class="text-start fw-bold me-2">Basic Details Form (BDF)</h6>
            <p class="mb-0 text-muted" style="font-size: small">
              - Fields with <span class="text-danger">*</span> are required.
            </p>
          </div>

          <div
            v-if="errorMessage"
            class="alert alert-danger alert-dismissible fade show d-flex justify-content-between"
            role="alert"
          >
            <div>
              <i class="bi bi-exclamation-triangle-fill me-2"></i>
              {{ errorMessage }}
            </div>

            <button
              type="button"
              class="btn-close"
              data-bs-dismiss="alert"
              aria-label="Close"
            ></button>
          </div>

          <div class="row text-start">
            <div class="col-md-4 mb-3">
              <label for="programType" class="form-label">
                Program Type <span class="text-danger">*</span>
              </label>
              <select
                v-model="programType"
                class="form-select text-capitalize"
                id="programType"
                @change="fetchProgramsByCriteria"
                :disabled="loading || !applicationAvailable"
                required
              >
                <option value="" disabled selected>--Select--</option>
                <option
                  v-for="type in programTypes"
                  :key="type._id"
                  :value="type._id"
                >
                  {{ type.description }}
                </option>
              </select>
            </div>
            <div class="col-md-4 mb-3">
              <label for="programMode" class="form-label">
                Program Mode <span class="text-danger">*</span>
              </label>
              <select
                v-model="programMode"
                class="form-select text-capitalize"
                id="programMode"
                :disabled="!programType || loading || !applicationAvailable"
                @change="fetchProgramsByCriteria"
                required
              >
                <option value="" disabled selected>--Select--</option>
                <option
                  v-for="mode in programModes"
                  :key="mode._id"
                  :value="mode._id"
                >
                  {{ mode.description }}
                </option>
              </select>
            </div>
            <div class="col-md-4 mb-3">
              <label for="program" class="form-label">
                Program <span class="text-danger">*</span>
              </label>
              <select
                v-model="program"
                class="form-select text-capitalize"
                id="program"
                :disabled="
                  !programMode ||
                  loading ||
                  !applicationAvailable ||
                  fetchingPrograms ||
                  availablePrograms.length === 0
                "
                required
              >
                <option value="" disabled selected>
                  {{
                    fetchingPrograms
                      ? "Loading..."
                      : availablePrograms.length === 0 && programMode
                      ? "No programs available"
                      : "--Select--"
                  }}
                </option>
                <option
                  v-for="program in availablePrograms"
                  :key="program._id"
                  :value="program._id"
                >
                  {{ program.name }}
                </option>
              </select>
            </div>
          </div>

          <div class="row text-start">
            <div class="col-md-4 mb-3">
              <label for="surname" class="form-label">
                Surname <span class="text-danger">*</span>
              </label>
              <input
                type="text"
                class="form-control"
                v-model="surname"
                id="surname"
                placeholder="Doe"
                :disabled="loading || !applicationAvailable"
                required
              />
            </div>
            <div class="col-md-4 mb-3">
              <label for="firstName" class="form-label">
                First Name <span class="text-danger">*</span>
              </label>
              <input
                type="text"
                class="form-control"
                v-model="firstname"
                id="firstName"
                placeholder="John"
                :disabled="loading || !applicationAvailable"
                required
              />
            </div>
            <div class="col-md-4 mb-3">
              <label for="otherName" class="form-label">Other Name</label>
              <input
                type="text"
                class="form-control"
                id="otherName"
                v-model="othername"
                placeholder="Shabba"
                :disabled="loading || !applicationAvailable"
              />
            </div>
          </div>

          <div class="row text-start">
            <div class="col-md-6 mb-3">
              <label for="mobileNumber" class="form-label">
                Mobile Number <span class="text-danger">*</span>
              </label>
              <input
                type="tel"
                v-model="phone"
                class="form-control"
                id="mobileNumber"
                placeholder="08012345678"
                :disabled="loading || !applicationAvailable"
                required
              />
            </div>
            <div class="col-md-6 mb-3">
              <label for="email" class="form-label">
                Email <span class="text-danger">*</span>
              </label>
              <input
                type="email"
                v-model="email"
                class="form-control"
                id="email"
                placeholder="johndoe@mail.com"
                :disabled="loading || !applicationAvailable"
                required
              />
            </div>
            <div class="col-md-6 mb-3">
              <label for="password" class="form-label">
                Password <span class="text-danger">*</span></label
              >
              <input
                id="password"
                v-model="password"
                type="password"
                class="form-control"
                minlength="8"
                autocomplete="new-password"
                :disabled="loading || !applicationAvailable"
                required
              />
              <small class="text-muted">Use at least 8 characters.</small>
            </div>
            <div class="col-md-6 mb-3">
              <label for="confirmPassword" class="form-label">
                Confirm Password <span class="text-danger">*</span></label
              >
              <input
                id="confirmPassword"
                v-model="confirmPassword"
                type="password"
                class="form-control"
                minlength="8"
                autocomplete="new-password"
                :disabled="loading || !applicationAvailable"
                required
              />
            </div>
          </div>

          <div class="d-grid gap-2 d-md-flex justify-content-md-start mt-4">
            <button
              type="submit"
              class="btn btn-primary"
              :disabled="loading || !applicationAvailable"
            >
              {{ loading ? "Applying..." : "Apply" }}
            </button>
            <button
              class="btn btn-outline-secondary"
              type="button"
              @click="resetForm"
              :disabled="loading || !applicationAvailable"
            >
              Clear
            </button>
          </div>

          <p class="mt-3">
            Already registered?
            <a
              :href="$urlHelper.getPortalUrl('/')"
              class="link-primary link-offset-2 link-underline-opacity-25 link-underline-opacity-100-hover"
            >
              Sign in
            </a>
          </p>

          <p class="mt-5 mb-3 text-body-secondary border-top pt-3">
            © 2022–{{ new Date().getFullYear() }}
          </p>
        </form>
      </main>
    </div>

    <!-- Login Details Display Modal -->
    <div
      class="modal fade"
      id="displayModal"
      tabindex="-1"
      aria-labelledby="displayModalLabel"
      aria-hidden="true"
    >
      <div class="modal-dialog modal-fullscreen">
        <div class="modal-content fcp-bg-secondary">
          <div class="modal-header border-bottom-0">
            <button
              type="button"
              class="btn-close btn-close-white"
              data-bs-dismiss="modal"
              aria-label="Close"
            ></button>
          </div>

          <div
            class="modal-body text-center text-white d-flex flex-column align-items-center justify-content-center"
          >
            <i class="bi bi-patch-check-fill h1"></i>
            <h1 class="fs-4 fw-bold">Registration Successful</h1>

            <p class="fw-light mb-5">
              Please save your login details securely. You’ll need them to
              access the student portal.
            </p>

            <div class="rounded p-4 text-start border d-inline-block mb-2">
              <p>
                <span>Application No: </span>
                <span class="fw-bold">{{
                  loginDetails.applicationNumber
                }}</span>
                <i
                  class="bi bi-copy ms-4 cursor-pointer"
                  @click="copyToClipboard(loginDetails.applicationNumber)"
                ></i>
              </p>

              <!-- <p>
                <span>Email:</span> {{ loginDetails.email }}
                <i
                  class="bi bi-copy ms-4 cursor-pointer"
                  @click="copyToClipboard(loginDetails.email)"
                ></i>
              </p> -->

              <p>Use the password you created to sign in.</p>

              <button
                type="button"
                class="btn btn-outline-primary btn-sm"
                @click="downloadPDF"
              >
                <i class="bi bi-download me-1"></i> Download PDF
              </button>
            </div>
            <small class="text-danger d-block mb-5">
              Keep your application number and password secure.
            </small>

            <div>
              <a
                :href="$urlHelper.getPortalUrl('/')"
                class="link-offset-2 link-offset-3-hover link-underline link-underline-opacity-0 link-underline-opacity-75-hover"
                @click="resetForm"
              >
                <i class="bi bi-arrow-left"></i> Go to log in
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}

.cursor-pointer:hover {
  opacity: 0.8;
}
</style>
